import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  FeatureFlagsService,
  FeatureFlagFetchResponse,
} from '../../services/feature-flags';
import { redirectToLogin } from '../../lib/auth';
import { AxiosError } from 'axios';
import { useEffect } from 'react';

/**
 * Query key for feature flags
 */
export const FEATURE_FLAGS_QUERY_KEY = ['featureFlags'];

/**
 * Fetch feature flags from patron server
 *
 * @param programId - The program ID to fetch feature flags for
 * @param options - Optional React Query options
 * @returns Query result with feature flags data
 */
export const useFeatureFlagsQuery = (
  programId: string,
  options?: Omit<
    UseQueryOptions<
      FeatureFlagFetchResponse,
      Error,
      FeatureFlagFetchResponse,
      string[]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const query = useQuery<
    FeatureFlagFetchResponse,
    Error,
    FeatureFlagFetchResponse,
    string[]
  >({
    queryKey: [...FEATURE_FLAGS_QUERY_KEY, programId],
    queryFn: async () => {
      const service = new FeatureFlagsService();
      return await service.fetch();
    },
    enabled: !!programId,
    keepPreviousData: true,
    ...options,
  });

  useEffect(() => {
    if (query.error && query.error instanceof AxiosError) {
      if (
        query.error.response?.status === 401 ||
        query.error.response?.status === 403
      ) {
        redirectToLogin();
      }
    }
  }, [query.error]);
  return query;
};
